import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Trash2,
  Play,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  AlertTriangle,
  Ship,
  CalendarDays,
  Clock,
  Loader2,
  Info,
  Shield
} from 'lucide-react';
import { useTaxYear } from '../context/TaxYearContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Simulator = () => {
  const { selectedYear } = useTaxYear();
  const [trips, setTrips] = useState([
    { _key: crypto.randomUUID(), date_left_uk: '', date_returned_uk: '', trip_type: 'work_trip', vessel_name: '' }
  ]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [breakeven, setBreakeven] = useState(null);
  const [breakevenLoading, setBreakevenLoading] = useState(false);

  // Fetch breakeven on mount / year change
  React.useEffect(() => {
    fetchBreakeven();
  }, [selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBreakeven = async () => {
    setBreakevenLoading(true);
    try {
      const params = selectedYear && selectedYear !== 'all' ? `?tax_year=${selectedYear}` : '';
      const res = await axios.get(`${API_URL}/api/simulator/breakeven${params}`);
      setBreakeven(res.data);
    } catch (err) {
      // ignore
    } finally {
      setBreakevenLoading(false);
    }
  };

  const addTrip = () => {
    setTrips([...trips, { _key: crypto.randomUUID(), date_left_uk: '', date_returned_uk: '', trip_type: 'work_trip', vessel_name: '' }]);
  };

  const removeTrip = (index) => {
    setTrips(trips.filter((_, i) => i !== index));
  };

  const updateTrip = (index, field, value) => {
    const updated = [...trips];
    updated[index] = { ...updated[index], [field]: value };
    setTrips(updated);
  };

  const runSimulation = async () => {
    const validTrips = trips.filter(t => t.date_left_uk && t.date_returned_uk);
    if (validTrips.length === 0) {
      toast.error('Add at least one trip with both dates');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/simulator/what-if`, {
        tax_year: selectedYear && selectedYear !== 'all' ? selectedYear : undefined,
        hypothetical_trips: validTrips
      });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Simulation failed');
    } finally {
      setLoading(false);
    }
  };

  const StatCompare = ({ label, current, projected, suffix = '', isMoney = false }) => {
    const diff = projected - current;
    const improved = diff > 0;
    const formatVal = (v) => isMoney ? `£${v.toLocaleString()}` : v;
    return (
      <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
        <p className="text-xs text-slate-500 mb-2">{label}</p>
        <div className="flex items-center gap-2">
          <span className="text-white font-semibold">{formatVal(current)}{suffix}</span>
          <ArrowRight className="h-3.5 w-3.5 text-slate-500" />
          <span className={`font-bold ${improved ? 'text-emerald-400' : diff < 0 ? 'text-red-400' : 'text-white'}`}>
            {formatVal(projected)}{suffix}
          </span>
          {diff !== 0 && (
            <Badge className={`text-[10px] px-1.5 py-0 ${improved ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
              {diff > 0 ? '+' : ''}{isMoney ? `£${diff.toLocaleString()}` : diff}{suffix}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="simulator-page">
      <div>
        <h1 className="text-2xl font-bold text-white">What-If Simulator</h1>
        <p className="text-slate-400 mt-1">
          Model hypothetical future trips and see how they affect your SED eligibility
        </p>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardContent className="p-3 flex items-start gap-3">
          <Info className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-slate-400">
            Add one or more hypothetical trips below to see how they would impact your qualifying days, 
            days in hand, and SED eligibility. This won't affect your actual data.
          </p>
        </CardContent>
      </Card>

      {/* Breakeven Calculator */}
      {breakeven && !breakevenLoading && (
        <Card className={`border ${
          breakeven.sed_eligible
            ? 'bg-emerald-500/5 border-emerald-500/20'
            : breakeven.days_needed_for_eligibility > 0
              ? 'bg-amber-500/5 border-amber-500/20'
              : 'bg-slate-800 border-slate-700'
        }`} data-testid="breakeven-card">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <CalendarDays className={`h-5 w-5 ${breakeven.sed_eligible ? 'text-emerald-400' : 'text-amber-400'}`} />
              <p className="text-white font-semibold text-sm">Eligibility Snapshot — {breakeven.tax_year}</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-900/50 rounded-lg p-2.5 border border-slate-700/40">
                <p className="text-[10px] text-slate-500 uppercase">Qualifying Days</p>
                <p className="text-lg font-bold text-white">{breakeven.qualifying_days}</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-2.5 border border-slate-700/40">
                <p className="text-[10px] text-slate-500 uppercase">Days in Hand</p>
                <p className={`text-lg font-bold ${breakeven.days_in_hand >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {breakeven.days_in_hand >= 0 ? '+' : ''}{breakeven.days_in_hand}
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-2.5 border border-slate-700/40">
                <p className="text-[10px] text-slate-500 uppercase">Days Needed</p>
                <p className={`text-lg font-bold ${breakeven.days_needed_for_eligibility > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {breakeven.days_needed_for_eligibility > 0 ? breakeven.days_needed_for_eligibility : '0'}
                </p>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-2.5 border border-slate-700/40">
                <p className="text-[10px] text-slate-500 uppercase">Year Remaining</p>
                <p className="text-lg font-bold text-slate-300">{breakeven.days_remaining_in_tax_year}d</p>
              </div>
            </div>
            {breakeven.advice.length > 0 && (
              <div className="space-y-1">
                {breakeven.advice.map((a, i) => (
                  <p key={`advice-${a.substring(0, 20)}-${i}`} className="text-xs text-slate-400 flex items-start gap-1.5">
                    <span className="text-amber-400 mt-0.5">&#8226;</span>{a}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Hypothetical Trips */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Ship className="h-5 w-5 text-amber-500" />
            Hypothetical Trips
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {trips.map((trip, index) => (
            <div key={trip._key} className="bg-slate-700/30 rounded-lg p-3 border border-slate-600/50 space-y-3" data-testid={`hyp-trip-${index}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-300 font-medium">Trip {index + 1}</p>
                {trips.length > 1 && (
                  <Button size="sm" variant="ghost" onClick={() => removeTrip(index)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
                    data-testid={`remove-trip-${index}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-slate-400 text-xs">Leave UK</Label>
                  <Input type="date" value={trip.date_left_uk}
                    onChange={(e) => updateTrip(index, 'date_left_uk', e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white h-9 mt-1"
                    data-testid={`trip-leave-${index}`} />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Return UK</Label>
                  <Input type="date" value={trip.date_returned_uk}
                    onChange={(e) => updateTrip(index, 'date_returned_uk', e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white h-9 mt-1"
                    data-testid={`trip-return-${index}`} />
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Trip Type</Label>
                  <Select value={trip.trip_type} onValueChange={(val) => updateTrip(index, 'trip_type', val)}>
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white h-9 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-600">
                      <SelectItem value="work_trip" className="text-white hover:bg-slate-700">Work Trip</SelectItem>
                      <SelectItem value="qualifying_holiday" className="text-white hover:bg-slate-700">Holiday (Abroad)</SelectItem>
                      <SelectItem value="course_foreign" className="text-white hover:bg-slate-700">Course (Abroad)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Vessel/Location</Label>
                  <Input value={trip.vessel_name} placeholder="e.g. MV Example"
                    onChange={(e) => updateTrip(index, 'vessel_name', e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white h-9 mt-1"
                    data-testid={`trip-vessel-${index}`} />
                </div>
              </div>
              {trip.date_left_uk && trip.date_returned_uk && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <CalendarDays className="h-3 w-3" />
                  {Math.ceil((new Date(trip.date_returned_uk) - new Date(trip.date_left_uk)) / 86400000)} days abroad
                </div>
              )}
            </div>
          ))}

          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={addTrip}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="add-hyp-trip-btn">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Another Trip
            </Button>
            <Button onClick={runSimulation} disabled={loading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="run-simulation-btn">
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Simulating...</>
              ) : (
                <><Play className="h-4 w-4 mr-2" />Run Simulation</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Impact Banner */}
          <Card className={`border ${
            result.impact.claim_risk 
              ? 'bg-red-500/10 border-red-500/30' 
              : result.impact.eligibility_changed && result.projected.sed_eligible
                ? 'bg-emerald-500/10 border-emerald-500/30'
                : 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <CardContent className="p-4 flex items-center gap-3">
              {result.impact.claim_risk ? (
                <>
                  <AlertTriangle className="h-6 w-6 text-red-400 flex-shrink-0" />
                  <div>
                    <p className="text-red-400 font-semibold">Warning: This could break your SED claim</p>
                    <p className="text-xs text-red-300/70 mt-0.5">The UK days would exceed the half-day threshold in the qualifying period.</p>
                  </div>
                </>
              ) : result.impact.eligibility_changed && result.projected.sed_eligible ? (
                <>
                  <CheckCircle2 className="h-6 w-6 text-emerald-400 flex-shrink-0" />
                  <div>
                    <p className="text-emerald-400 font-semibold">These trips would make you SED eligible!</p>
                    <p className="text-xs text-emerald-300/70 mt-0.5">Adding {result.impact.additional_qualifying_days} qualifying days pushes you over the threshold.</p>
                  </div>
                </>
              ) : (
                <>
                  <Shield className="h-6 w-6 text-amber-400 flex-shrink-0" />
                  <div>
                    <p className="text-amber-400 font-semibold">
                      +{result.impact.additional_qualifying_days} qualifying days | {result.impact.days_in_hand_change >= 0 ? '+' : ''}{result.impact.days_in_hand_change} days in hand
                    </p>
                    <p className="text-xs text-amber-300/70 mt-0.5">
                      {result.projected.sed_eligible ? 'SED eligibility maintained.' : 'Still building towards eligibility.'}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Current vs Projected Comparison */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-3">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <TrendingUp className="h-5 w-5 text-amber-500" />
                Current vs Projected
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <StatCompare label="Qualifying Days" current={result.current.qualifying_days} projected={result.projected.qualifying_days} />
                <StatCompare label="Days in Hand" current={result.current.days_in_hand} projected={result.projected.days_in_hand} />
                <StatCompare label="Total Trips" current={result.current.trips_count} projected={result.projected.trips_count} />
                <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                  <p className="text-xs text-slate-500 mb-2">SED Eligibility</p>
                  <div className="flex items-center gap-2">
                    <Badge className={result.current.sed_eligible ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                      {result.current.sed_eligible ? 'Eligible' : 'Building'}
                    </Badge>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-500" />
                    <Badge className={result.projected.sed_eligible ? 'bg-emerald-500/20 text-emerald-400' : result.projected.claim_broken ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}>
                      {result.projected.sed_eligible ? 'Eligible' : result.projected.claim_broken ? 'Broken' : 'Building'}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Hypothetical Trips Summary */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-slate-400" />
                Modelled Trips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {result.hypothetical_trips.map((ht, i) => (
                  <div key={`${ht.date_left_uk}-${ht.date_returned_uk}`} className="flex items-center justify-between bg-slate-700/30 rounded-lg px-3 py-2 border border-slate-600/30">
                    <div className="flex items-center gap-3">
                      <Ship className="h-4 w-4 text-amber-400" />
                      <div>
                        <p className="text-sm text-white">{ht.vessel_name || 'Hypothetical Trip'}</p>
                        <p className="text-xs text-slate-500">{ht.date_left_uk} to {ht.date_returned_uk}</p>
                      </div>
                    </div>
                    <Badge className="bg-blue-500/20 text-blue-400">{ht.days_abroad} days</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Simulator;
